import React from "react";
import styled from "@emotion/styled";
import {useTranslation} from "react-i18next";
import {PrimaryButton, PureButton} from "components/Common";
import {ReactComponent as CloseIcon} from "assets/images/icons/close.svg";
import {getIsContextualPromptOpen} from "modules/selectors";
import {useDispatch, useSelector} from "react-redux";
import {closeContextualPrompt} from "modules/actions";
import {breakpoints} from "assets/theme";
import {keyframes} from "@emotion/react";
import Cookies from "js-cookie";

const slideIn = keyframes`
	from {
		transform: translateX(-100%);
	}
	to {
		transform: translateX(0);
	}
`;

const Wrapper = styled.a`
	background-color: ${({theme}) => theme.colorPalette.secondary};
	display: block;
	position: sticky;
	top: 0;
	z-index: 9;
	animation: ${slideIn} 0.4s ease-in-out forwards;
`;

const Inner = styled.div`
	max-width: 1240px;
	padding: 12px 20px;
	margin: 0 auto;

	.desktop {
		display: none;
	}

	@media (min-width: ${breakpoints.mobile}) {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 20px;
		padding: 8px 20px;

		.mobile {
			display: none;
		}

		.desktop {
			display: block;
		}
	}
`;

const TitleWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	margin-bottom: 8px;

	@media (min-width: ${breakpoints.mobile}) {
		margin-bottom: 0;
	}
`;

const Title = styled.h2`
	font-size: 18px;
	color: ${({theme}) => theme.colorPalette.black};
	font-weight: 700;
	line-height: 120%;
	letter-spacing: 0.2px;

	@media (min-width: ${breakpoints.mobile}) {
		font-size: 20px;
	}
`;

const Description = styled.p`
	font-size: 12px;
	color: ${({theme}) => theme.colorPalette.black};
	font-weight: 500;
	line-height: 132%;
	letter-spacing: 0.24px;
	flex: 1;
`;

const ButtonWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;

	@media (min-width: ${breakpoints.mobile}) {
		gap: 40px;
	}
`;

export const ContextualPrompt: React.FC = () => {
	const {t} = useTranslation();
	const dispatch = useDispatch();
	const isOpen = useSelector(getIsContextualPromptOpen);
	const hasSeenBanner = Cookies.get("contextual-prompt") === "true";

	const setBannerViewed = () => {
		Cookies.set("contextual-prompt", "true", {
			expires: 1,
		});
	};

	const onClose = (e: React.SyntheticEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		e.preventDefault();

		setBannerViewed();
		dispatch(closeContextualPrompt());
	};

	if (!isOpen || hasSeenBanner) {
		return null;
	}

	return (
		<Wrapper href={`${window.location.origin}/pickem/`} onClick={setBannerViewed}>
			<Inner>
				<TitleWrapper>
					<Title>{t("prompt.pickem.title", "Play CFL Pick’em Today")}</Title>

					<Description className="desktop">
						{t(
							"prompt.pickem.description",
							"Make your picks and compete against friends and fans across the country."
						)}
					</Description>

					<PureButton className="mobile" onClick={onClose}>
						<CloseIcon />
					</PureButton>
				</TitleWrapper>

				<ButtonWrapper>
					<Description className="mobile">
						{t(
							"prompt.pickem.description",
							"Make your picks and compete against friends and fans across the country."
						)}
					</Description>

					<PrimaryButton>{t("prompt.pickem.button", "PLAY NOW")}</PrimaryButton>

					<PureButton className="desktop" onClick={onClose}>
						<CloseIcon />
					</PureButton>
				</ButtonWrapper>
			</Inner>
		</Wrapper>
	);
};
